/**
 * BillingPage — Standalone billing and subscription management page.
 *
 * EU-compliant subscription cancellation with 2-click max flow.
 * Sections:
 *  A) Subscription Overview
 *  B) Cancel Subscription (EU compliant)
 *  C) Invoice History
 */

import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { getSubscription, cancelSubscription, getInvoices, requestWithdrawal, getCertSubscriptions, cancelCertSubscription, requestCertWithdrawal } from '../api/billing';
import type { SubscriptionInfo, InvoiceItem } from '../types/billing';
import type { CertSubscription } from '../api/billing';
import ConfirmModal from '../components/ConfirmModal';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { Button, Card, Chip, LinkButton, AppShell } from '../components/ui';
import type { ChipTone } from '../components/ui';

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatPlanName(plan: string, t: TFunction): string {
  switch (plan) {
    case 'premium_monthly': return t('account.billing.plan.premium_monthly');
    case 'premium_yearly': return t('account.billing.plan.premium_yearly');
    case 'free': return t('account.billing.plan.free');
    default: return plan;
  }
}

function formatStatus(status: string, t: TFunction): string {
  const key = `account.billing.status.${status}`;
  const translated = t(key);
  if (translated && translated !== key) return translated;
  // Fallback: capitalize and replace underscores
  return status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ');
}

function formatAmount(amountCents: number, currency: string): string {
  const major = (amountCents / 100).toFixed(2).replace('.', ',');
  return `${major} ${currency.toUpperCase()}`;
}

/** Map a Stripe-style status string onto the shared `<Chip>` tone palette. */
function statusTone(status: string): ChipTone {
  switch (status) {
    case 'active':
    case 'paid':
      return 'green';
    case 'trialing':
    case 'pending':
    case 'open':
      return 'amber';
    case 'past_due':
    case 'failed':
    case 'canceled':
    case 'uncollectible':
      return 'red';
    default:
      return 'neutral';
  }
}

// ── Icon ─────────────────────────────────────────────────────────────────────

function CreditCardIcon() {
  return (
    <svg className="h-6 w-6 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z" />
    </svg>
  );
}

// ── Toast ────────────────────────────────────────────────────────────────────

type Toast = { type: 'success' | 'error'; message: string } | null;

function ToastNotification({ toast, onClose }: { toast: Toast; onClose: () => void }) {
  const { t } = useTranslation();
  if (!toast) return null;

  return (
    <div
      className={`fixed bottom-6 right-6 z-50 flex animate-in items-center gap-3 rounded-xl px-5 py-3.5 text-sm font-medium text-white shadow-[0_12px_32px_-12px_rgba(15,23,42,0.45)] slide-in-from-bottom-4
        ${toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'}`}
    >
      {toast.type === 'success' ? (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
        </svg>
      ) : (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      )}
      {toast.message}
      <button onClick={onClose} className="ml-2 opacity-80 transition-opacity hover:opacity-100" aria-label={t('account.billing.toast.close')}>
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

// ── Per-Certification Subscriptions ───────────────────────────────────────────

/** Check whether the subscription was created within the last 14 days. */
function isWithin14Days(createdAt: string | null): boolean {
  if (!createdAt) return false;
  const created = new Date(createdAt);
  const now = new Date();
  const diffDays = (now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24);
  return diffDays <= 14;
}

function CertSubscriptionsSection() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [cancellingCert, setCancellingCert] = useState<string | null>(null);
  const [withdrawingCert, setWithdrawingCert] = useState<string | null>(null);
  const [certToast, setCertToast] = useState<string | null>(null);

  const { data: certSubs, isLoading } = useQuery<CertSubscription[]>({
    queryKey: ['billing', 'cert-subscriptions'],
    queryFn: getCertSubscriptions,
    staleTime: 30_000,
  });

  const activeSubs = (certSubs ?? []).filter(
    (s) => s.status === 'active' || s.status === 'canceling'
  );

  if (isLoading || activeSubs.length === 0) return null;

  const handleCancelCert = async (certCode: string) => {
    setCancellingCert(certCode);
    try {
      const result = await cancelCertSubscription(certCode);
      setCertToast(result.message);
      queryClient.invalidateQueries({ queryKey: ['billing', 'cert-subscriptions'] });
      setTimeout(() => setCertToast(null), 4000);
    } catch {
      setCertToast(t('account.billing.certSubs.cancelError'));
      setTimeout(() => setCertToast(null), 4000);
    } finally {
      setCancellingCert(null);
    }
  };

  const handleCertWithdrawal = async (certCode: string) => {
    setWithdrawingCert(certCode);
    try {
      const result = await requestCertWithdrawal(certCode);
      setCertToast(result.message);
      queryClient.invalidateQueries({ queryKey: ['billing', 'cert-subscriptions'] });
      setTimeout(() => setCertToast(null), 6000);
    } catch (err: unknown) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
        ?? t('account.billing.certSubs.withdrawError');
      setCertToast(detail);
      setTimeout(() => setCertToast(null), 6000);
    } finally {
      setWithdrawingCert(null);
    }
  };

  return (
    <Card className="space-y-4 p-6">
      <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
        {t('account.billing.certSubs.title')}
      </h2>

      {certToast && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-2.5 text-sm text-blue-700 dark:border-blue-500/20 dark:bg-blue-500/10 dark:text-blue-300">
          {certToast}
        </div>
      )}

      <div className="space-y-3">
        {activeSubs.map((sub) => {
          const canWithdraw = sub.status === 'active' && isWithin14Days(sub.created_at);
          return (
            <Card
              key={sub.certification_code}
              variant="subtle"
              className="flex items-center justify-between px-4 py-3"
            >
              <div>
                <span className="text-sm font-semibold uppercase text-slate-900 dark:text-white">
                  {sub.certification_code}
                </span>
                <Chip tone={statusTone(sub.status)} className="ml-2">
                  {formatStatus(sub.status, t)}
                </Chip>
                {sub.current_period_end && (
                  <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                    {t('account.billing.certSubs.until', { date: new Date(sub.current_period_end).toLocaleDateString() })}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                {canWithdraw && (
                  <button
                    type="button"
                    onClick={() => handleCertWithdrawal(sub.certification_code)}
                    disabled={withdrawingCert === sub.certification_code}
                    className="rounded-lg border border-blue-300 px-3 py-1.5 text-xs font-medium text-blue-700 transition-colors hover:bg-blue-50 disabled:opacity-50 dark:border-blue-500/30 dark:text-blue-300 dark:hover:bg-blue-500/10"
                    title={t('account.billing.certSubs.withdrawTitle')}
                  >
                    {withdrawingCert === sub.certification_code ? t('account.billing.certSubs.processing') : t('account.billing.certSubs.withdraw')}
                  </button>
                )}
                {sub.status === 'active' && (
                  <button
                    type="button"
                    onClick={() => handleCancelCert(sub.certification_code)}
                    disabled={cancellingCert === sub.certification_code}
                    className="rounded-lg border border-red-300 px-3 py-1.5 text-xs font-medium text-red-600 transition-colors hover:bg-red-50 disabled:opacity-50 dark:border-red-500/30 dark:text-red-300 dark:hover:bg-red-500/10"
                  >
                    {cancellingCert === sub.certification_code ? t('account.billing.certSubs.cancelling') : t('account.billing.certSubs.cancel')}
                  </button>
                )}
              </div>
            </Card>
          );
        })}
      </div>

      <Link
        to="/pricing"
        className="inline-block text-sm font-medium text-blue-600 hover:underline dark:text-blue-400"
      >
        {t('account.billing.certSubs.addMore')}
      </Link>
    </Card>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────

export default function BillingPage() {
  const { t } = useTranslation();
  useDocumentTitle(t('account.billing.documentTitle'));

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [toast, setToast] = useState<Toast>(null);

  const { data: subscription, isLoading: subLoading, error: subError } = useQuery<SubscriptionInfo>({
    queryKey: ['billing', 'subscription'],
    queryFn: getSubscription,
    staleTime: 30_000,
  });

  const { data: invoiceData, isLoading: invLoading } = useQuery<{ invoices: InvoiceItem[] }>({
    queryKey: ['billing', 'invoices'],
    queryFn: () => getInvoices(),
    staleTime: 60_000,
  });

  const isPremiumActive =
    subscription &&
    subscription.plan !== 'free' &&
    (subscription.status === 'active' || subscription.status === 'trialing' || subscription.status === 'none');

  const invoices = invoiceData?.invoices ?? [];

  // EU 14-day withdrawal eligibility check:
  // The subscription must be active and the period_start must be within the last 14 days.
  const isWithinWithdrawalWindow = (() => {
    if (!isPremiumActive || !subscription?.period_start) return false;
    const start = new Date(subscription.period_start);
    const now = new Date();
    const diffDays = (now.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
    return diffDays <= 14;
  })();

  const handleWithdrawalConfirm = async () => {
    setWithdrawing(true);
    try {
      const result = await requestWithdrawal();
      await queryClient.invalidateQueries({ queryKey: ['billing', 'subscription'] });
      setShowWithdrawalModal(false);
      setToast({ type: 'success', message: result.message });
      setTimeout(() => setToast(null), 6000);
    } catch (err: unknown) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
        ?? t('account.billing.withdrawalError');
      setToast({ type: 'error', message: detail });
      setTimeout(() => setToast(null), 6000);
    } finally {
      setWithdrawing(false);
    }
  };

  const handleCancelConfirm = async () => {
    setCancelling(true);
    try {
      await cancelSubscription();
      await queryClient.invalidateQueries({ queryKey: ['billing', 'subscription'] });
      setShowCancelModal(false);
      setToast({ type: 'success', message: t('account.billing.cancelSuccess') });
      setTimeout(() => setToast(null), 4000);
    } catch {
      setToast({ type: 'error', message: t('account.billing.cancelError') });
      setTimeout(() => setToast(null), 4000);
    } finally {
      setCancelling(false);
    }
  };

  return (
    <AppShell maxWidth="7xl" mainClassName="!max-w-3xl">
      {/* Page header */}
      <div className="mb-8">
        <button
          onClick={() => navigate(-1)}
          className="mb-3 flex items-center gap-1.5 text-sm text-slate-500 transition-colors hover:text-blue-600 dark:text-slate-400 dark:hover:text-blue-400"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
          </svg>
          {t('account.billing.back')}
        </button>
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 dark:bg-blue-500/15">
            <CreditCardIcon />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
              {t('account.billing.pageTitleStart')}{' '}
              <span className="text-blue-600 dark:text-blue-400">
                {t('account.billing.pageTitleHighlight')}
              </span>
            </h1>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              {t('account.billing.pageSubtitle')}
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-6">

        {/* ── Section A: Subscription Overview ──────────────────────── */}
        <Card className="space-y-4 p-8">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">{t('account.billing.overviewTitle')}</h2>

          {subLoading && (
            <div className="flex items-center justify-center py-6">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-blue-600 border-t-transparent dark:border-blue-400 dark:border-t-transparent" />
            </div>
          )}

          {subError && (
            <div
              className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-400"
              role="alert"
            >
              {t('account.billing.loadError')}
            </div>
          )}

          {subscription && !subLoading && (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-lg font-bold text-slate-900 dark:text-white">
                  {formatPlanName(subscription.plan, t)}
                </span>
                <Chip tone={statusTone(subscription.status)}>
                  {formatStatus(subscription.status, t)}
                </Chip>
              </div>

              {subscription.amount_cents != null && subscription.interval && (
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {formatAmount(subscription.amount_cents, subscription.currency)} / {subscription.interval}
                </p>
              )}

              {subscription.period_start && subscription.period_end && (
                <div className="space-y-1 text-sm text-slate-600 dark:text-slate-400">
                  <p>
                    {t('account.billing.billingPeriod', {
                      start: new Date(subscription.period_start).toLocaleDateString(),
                      end: new Date(subscription.period_end).toLocaleDateString(),
                    })}
                  </p>
                  {!subscription.cancel_at_period_end && isPremiumActive && (
                    <p>{t('account.billing.nextBillingDate', { date: new Date(subscription.period_end).toLocaleDateString() })}</p>
                  )}
                </div>
              )}

              {subscription.cancel_at_period_end && subscription.period_end && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300">
                  {t('account.billing.cancelNotice', { date: new Date(subscription.period_end).toLocaleDateString() })}
                </div>
              )}

              {subscription.plan === 'free' && (
                <LinkButton to="/pricing" variant="primary">
                  {t('account.billing.upgradeToPremium')}
                </LinkButton>
              )}
            </div>
          )}

          <p className="border-t border-slate-200 pt-2 text-[11px] text-slate-400 dark:border-white/10 dark:text-slate-500">
            {t('account.billing.legalFooter')}
          </p>
        </Card>

        {/* ── Section B1: EU 14-day Withdrawal Right ────────────────── */}
        {isWithinWithdrawalWindow && !subscription?.cancel_at_period_end && (
          <Card variant="subtle" className="space-y-3 border-blue-200 p-6 dark:border-blue-500/30">
            <h2 className="text-lg font-semibold text-blue-700 dark:text-blue-300">{t('account.billing.withdrawalTitle')}</h2>
            <p
              className="text-sm text-slate-600 dark:text-slate-400"
              dangerouslySetInnerHTML={{ __html: t('account.billing.withdrawalDescription') }}
            />
            <p
              className="text-xs text-slate-500 dark:text-slate-500"
              dangerouslySetInnerHTML={{ __html: t('account.billing.withdrawalLegal') }}
            />
            <button
              type="button"
              onClick={() => setShowWithdrawalModal(true)}
              className="rounded-xl border border-blue-300 px-5 py-2.5 text-sm font-medium text-blue-700 transition-colors hover:bg-blue-50 dark:border-blue-500/30 dark:text-blue-300 dark:hover:bg-blue-500/10"
            >
              {t('account.billing.withdrawalAction')}
            </button>
          </Card>
        )}

        {showWithdrawalModal && (
          <ConfirmModal
            title={t('account.billing.withdrawalModal.title')}
            message={t('account.billing.withdrawalModal.message')}
            confirmLabel={withdrawing ? t('account.billing.withdrawalModal.processing') : t('account.billing.withdrawalModal.confirm')}
            cancelLabel={t('account.billing.withdrawalModal.cancel')}
            variant="warning"
            onConfirm={handleWithdrawalConfirm}
            onCancel={() => setShowWithdrawalModal(false)}
          />
        )}

        {/* ── Section B2: Cancel Subscription (EU compliant) ─────────── */}
        {isPremiumActive && !subscription?.cancel_at_period_end && (
          <Card variant="subtle" className="space-y-3 border-red-200 p-6 dark:border-red-500/30">
            <h2 className="text-lg font-semibold text-red-600 dark:text-red-400">{t('account.billing.cancelTitle')}</h2>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              {t('account.billing.cancelDescription')}
            </p>
            <Button type="button" variant="danger" onClick={() => setShowCancelModal(true)}>
              {t('account.billing.cancelAction')}
            </Button>
          </Card>
        )}

        {showCancelModal && subscription && (
          <ConfirmModal
            title={t('account.billing.cancelModal.title')}
            message={subscription.period_end
              ? t('account.billing.cancelModal.messageWithEnd', {
                  plan: formatPlanName(subscription.plan, t),
                  date: new Date(subscription.period_end).toLocaleDateString(),
                })
              : t('account.billing.cancelModal.messageImmediate', { plan: formatPlanName(subscription.plan, t) })}
            confirmLabel={cancelling ? t('account.billing.cancelModal.cancelling') : t('account.billing.cancelModal.confirm')}
            cancelLabel={t('account.billing.cancelModal.cancel')}
            variant="warning"
            onConfirm={handleCancelConfirm}
            onCancel={() => setShowCancelModal(false)}
          />
        )}

        {/* ── Section: Per-Certification Subscriptions ─────────────── */}
        <CertSubscriptionsSection />

        {/* ── Section C: Invoice History ────────────────────────────── */}
        <Card className="space-y-4 p-6">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">{t('account.billing.invoices.title')}</h2>

          {invLoading && (
            <div className="flex items-center justify-center py-6">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-blue-600 border-t-transparent dark:border-blue-400 dark:border-t-transparent" />
            </div>
          )}

          {!invLoading && invoices.length === 0 && (
            <p className="py-4 text-center text-sm text-slate-500 dark:text-slate-400">
              {t('account.billing.invoices.empty')}
            </p>
          )}

          {!invLoading && invoices.length > 0 && (
            <div className="-mx-6 overflow-x-auto px-6">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wide text-slate-400 dark:border-white/10 dark:text-slate-500">
                    <th className="pb-2 pr-4">{t('account.billing.invoices.date')}</th>
                    <th className="pb-2 pr-4">{t('account.billing.invoices.invoice')}</th>
                    <th className="pb-2 pr-4">{t('account.billing.invoices.amount')}</th>
                    <th className="pb-2 pr-4">{t('account.billing.invoices.status')}</th>
                    <th className="pb-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-white/10">
                  {invoices.map((inv) => {
                    return (
                      <tr key={inv.id}>
                        <td className="whitespace-nowrap py-3 pr-4 text-slate-700 dark:text-slate-300">
                          {new Date(inv.created).toLocaleDateString()}
                        </td>
                        <td className="whitespace-nowrap py-3 pr-4 text-slate-700 dark:text-slate-300">
                          {inv.number ?? '--'}
                        </td>
                        <td className="whitespace-nowrap py-3 pr-4 font-medium text-slate-900 dark:text-white">
                          {formatAmount(inv.amount_paid, inv.currency)} {t('account.billing.invoices.taxInclusive')}
                        </td>
                        <td className="py-3 pr-4">
                          <Chip tone={statusTone(inv.status)}>
                            {formatStatus(inv.status, t)}
                          </Chip>
                        </td>
                        <td className="py-3 text-right">
                          {inv.invoice_pdf && (
                            <a
                              href={inv.invoice_pdf}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm font-medium text-blue-600 hover:underline dark:text-blue-400"
                            >
                              {t('account.billing.invoices.download')}
                            </a>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>

      </div>

      <ToastNotification toast={toast} onClose={() => setToast(null)} />
    </AppShell>
  );
}
